// ==========================================
// RequestAccessModel.js
// ==========================================
export class RequestAccessModel {
  /* ─────────────────────────────────────────────────────────────────────
     ALLOWED KEYS 
  ───────────────────────────────────────────────────────────────────── */
  static ALLOWED_KEYS = Object.freeze([
    // Applicant
    "name",
    "email",
    "company",
    "companyId",

    // Classification
    "role",
    "departmentId",
    "departmentName",

    // Location
    "location",
    "address1",
    "address2",
    "address3",
    "pincode",

    // Lifecycle
    "description",
    "status",
    "submittedAt",
    "requestedBy",

    // Audit
    "createdAt",
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  static ID_FIELD   = "email";
  static NAME_FIELD = "name";

  static VALID_ROLES    = Object.freeze(["user"]);
  static VALID_STATUSES = Object.freeze(["pending"]);

  /** Loose email check (mirrors HTML5 input[type=email]). */
  static EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** Company ID: 3–20 uppercase alphanumeric (matches service regex). */
  static COMPANY_ID_REGEX = /^[A-Z0-9]{3,20}$/;

  /** Pincode: exactly 6 digits (form's maxLength + service validation). */
  static PINCODE_REGEX = /^\d{6}$/;

  constructor(data = {}, { isCreation = false } = {}) {
    /* ── 1. Seed defaults ─────────────────────────────────────────────── */
    for (const key of RequestAccessModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null,
          deletedFlag: false,
        };
      } else if (key === "status") {
        this.status = "pending";
      } else if (key === "role") {
        this.role = "user";
      } else {
        this[key] = null;
      }
    }

    /* ── 2. Populate ──────────────────────────────────────────────────── */
    if (isCreation) {
      const now = new Date().toISOString();

      this.name           = data.name?.trim()                              || null;
      this.email          = data.email?.trim().toLowerCase()               || null;
      this.company        = data.company?.trim()                           || null;
      this.companyId      = data.companyId?.trim().toUpperCase()           || null;

      this.role           = data.role           || "user";
      this.departmentId   = data.departmentId   || null;
      this.departmentName = data.departmentName || null;

      this.location       = data.location?.trim() || data.city?.trim()     || null;
      this.address1       = data.address1?.trim()                          || null;
      this.address2       = data.address2?.trim()                          || null;
      this.address3       = data.address3?.trim()                          || null;
      this.pincode        = data.pincode?.trim()                           || null;

      this.description    = data.description?.trim()
                            || "Need access as a general user";
      this.status         = data.status         || "pending";
      this.submittedAt    = data.submittedAt    || now;
      this.requestedBy    = data.requestedBy    || null;

      this.createdAt      = data.createdAt      || now;

      this.metadata = {
        createdAt: data.metadata?.createdAt || this.createdAt,
        createdBy: data.metadata?.createdBy || this.email || "system",
        updatedAt: data.metadata?.updatedAt || this.createdAt,
        updatedBy: data.metadata?.updatedBy || this.email || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      /* ── Update path — copy only whitelisted keys ─────────────────── */
      for (const key of Object.keys(data)) {
        if (!RequestAccessModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(data.metadata || {}) };
        } else {
          this[key] = data[key];
        }
      }

      // Legacy top-level audit → metadata bridge
      if (data.createdAt) this.metadata.createdAt = data.createdAt;
      if (data.createdBy) this.metadata.createdBy = data.createdBy;
      if (data.updatedAt) this.metadata.updatedAt = data.updatedAt;
      if (data.updatedBy) this.metadata.updatedBy = data.updatedBy;
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     VALIDATION — mirrors validateRequestAccessForm() 1:1
     (DB company-existence check stays in the service — it's a network
      call, not a model concern.)
  ───────────────────────────────────────────────────────────────────── */
  isValid() {
    // Required
    if (!this.name      || !String(this.name).trim())      return false;
    if (!this.email     || !String(this.email).trim())     return false;
    if (!this.company   || !String(this.company).trim())   return false;
    if (!this.companyId || !String(this.companyId).trim()) return false;
    if (!this.address1  || !String(this.address1).trim())  return false;
    if (!this.location  || !String(this.location).trim())  return false;
    if (!this.pincode   || !String(this.pincode).trim())   return false;

    // Email format
    if (!RequestAccessModel.EMAIL_REGEX.test(this.email)) return false;

    // Company ID format
    if (!RequestAccessModel.COMPANY_ID_REGEX.test(this.companyId)) return false;

    // Pincode format
    if (!RequestAccessModel.PINCODE_REGEX.test(this.pincode)) return false;

    // Enum guards
    if (!RequestAccessModel.VALID_ROLES.includes(this.role))       return false;
    if (!RequestAccessModel.VALID_STATUSES.includes(this.status))  return false;

    return true;
  }

  /* ─────────────────────────────────────────────────────────────────────
     SERIALISATION
  ───────────────────────────────────────────────────────────────────── */
  toPayload() {
    const payload = {};
    for (const key of RequestAccessModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        payload.metadata = {};
        for (const mk of RequestAccessModel.ALLOWED_METADATA_KEYS) {
          payload.metadata[mk] =
            this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
        }
      } else if (this[key] !== undefined) {
        payload[key] = this[key];
      }
    }
    return payload;
  }

  toJSON() {
    return this.toPayload();
  }
}